import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked
} from '@angular/core';
import { IOtapUpgradeGatewayDTO, OtapUpgradeGatewayDTO } from '@app/shared/models/otap-upgrade-gateway.inerface';
import { OtapUpgradeService } from '@services/otap-upgrade/otap.upgrade.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { OtapUpgradeDTO } from '@app/shared/models/otap.upgrade.interface';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmComponent, ConfirmDialogData } from '@app/shared/components/dialogs/confirm/confirm.component';
import { ImportGatewaysDialogComponent } from './import-gateways-dialog.component';
import { Gateways, GatewayService } from 'src/app/shared/services/gateway.service';
import { from, of, throwError, timer } from 'rxjs';
import { concatMap, finalize, retry, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Gateway } from '@app/api/building/gateway/Gateway';
import { GatewayHealthStatusComponent } from '@components/gateway-health-status/gateway-health-status.component';
import { MatButtonModule } from '@angular/material/button';

type ExportState =
  | { status: 'idle' }
  | { status: 'starting' }
  | { status: 'waiting'; exportId: string }
  | { status: 'downloading'; exportId: string }
  | { status: 'completed'; exportId: string }
  | { status: 'failed'; message: string };

@Component({
  selector: 'app-gateway-details',
  templateUrl: './gateway-details.component.html',
  styleUrls: ['./gateway-details.component.scss'],
  imports: [GatewayHealthStatusComponent, ReactiveFormsModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GatewayDetailsComponent {
  readonly gateways = model.required<OtapUpgradeGatewayDTO[]>();
  readonly job = model.required<OtapUpgradeDTO>();
  readonly exportStatus = input<{ exportId: string; complete: boolean } | null>(null);
  readonly jobStartEnabled = output<boolean>();
  readonly exportCompleteEvent = output<{ exportId: string; complete: boolean }>();

  protected readonly Math = Math;
  public readonly serialNumberPattern = /^[\da-zA-Z-_\s]{16,256}$/;
  public readonly gatewayForm: FormGroup;
  readonly exportState = signal<ExportState>({ status: 'idle' });
  readonly exportInProgress = computed(() => {
    const status = this.exportState().status;
    return status === 'starting' || status === 'waiting' || status === 'downloading';
  });
  readonly exportError = computed(() => {
    const state = this.exportState();
    return state.status === 'failed' ? state.message : null;
  });

  private readonly importing = signal(false);
  private readonly otapUpgradeService = inject(OtapUpgradeService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly gatewayService = inject(GatewayService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.gatewayForm = this.formBuilder.group({
      newSerialNumber: ['', [Validators.required, Validators.pattern(this.serialNumberPattern)]]
    });
    effect(() => {
      const exportStatus = this.exportStatus();
      if (exportStatus?.complete) {
        untracked(() => this.updateExportStatusAndDownload(exportStatus.exportId, true));
      }
    });
  }

  public onAddGateway(): void {
    const newGateway: IOtapUpgradeGatewayDTO = {
      serialNumber: this.gatewayForm.value.newSerialNumber,
      jobId: this.job().id
    };

    this.otapUpgradeService.addGatewayToJob(this.job().id, newGateway).subscribe({
      next: (result) => {
        this.gateways.update(gateways => [...gateways, result]);
        this.gatewayForm.reset();
      },
      error: (err) => {
        alert('The gateway address you provided could not be added to this job. ' + err.error);
      }
    });
  }

  public onDelete(gateway: OtapUpgradeGatewayDTO): void {
    this.otapUpgradeService.deleteGatewayFromJob(gateway.jobId, gateway.id).subscribe({
      next: () => {
        this.gateways.update(gateways => gateways.filter(gw => gw.id !== gateway.id));
      },
      error: (error) => {
        alert('The delete request failed');
        console.error('There was an error during the delete request', error);
      }
    });
  }

  public onRebootGateway(rebootGateway: OtapUpgradeGatewayDTO): void {
    this.otapUpgradeService.rebootGateway(this.job().id, rebootGateway.id).subscribe((result) => {
      if (result === true) {
        this.gateways.update(gateways =>
          gateways.map(gateway => (gateway.id === rebootGateway.id ? this.markGatewayHealthUnknown(gateway) : gateway))
        );
      }
    });
  }

  public onRebootAllGateways(): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '400px',
      data: new ConfirmDialogData(
        'Are you sure you want to reboot <b>all gateways</b> in this job?',
        'Reboot All Gateways'
      )
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.otapUpgradeService.rebootAllGateways(this.job().id).subscribe((result) => {
          if (result === true) {
            this.gateways.update(gateways => gateways.map(gateway => this.markGatewayHealthUnknown(gateway)));
          }
        });
      }
    });
  }

  public onStopJob(): void {
    this.otapUpgradeService.stopJob(this.job().id).subscribe((result) => {
      if (result !== null) {
        this.job.set(result);
      }
    });
  }

  public onReopenJob(): void {
    this.otapUpgradeService.reopenJob(this.job().id).subscribe((result) => {
      if (result !== null) {
        this.job.set(result);

        this.jobStartEnabled.emit(true);
      }
    });
  }

  public onExportOtapProcess(): void {
    if (this.exportInProgress()) {
      return;
    }

    this.exportState.set({ status: 'starting' });
    this.otapUpgradeService
      .startExport(this.job().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (exportId) => {
          if (!exportId) {
            this.failExport('The export could not be started because no export id was returned.');
            return;
          }

          this.exportState.set({ status: 'waiting', exportId });
          this.exportCompleteEvent.emit({ exportId, complete: false });

          const exportStatus = this.exportStatus();
          if (exportStatus?.complete && exportStatus.exportId === exportId) {
            this.updateExportStatusAndDownload(exportId, true);
          }
        },
        error: (error) => {
          console.error('Unable to start OTAP export.', error);
          this.failExport('The export could not be started. Please try again.');
        }
      });
  }

  public downloadExportedFile(exportId: string): void {
    const state = this.exportState();
    if (!exportId || state.status !== 'waiting' || state.exportId !== exportId) {
      return;
    }

    this.exportState.set({ status: 'downloading', exportId });
    this.otapUpgradeService
      .downloadExport(this.job().id, exportId)
      .pipe(
        switchMap((data) => {
          if (!data.size) {
            return throwError(() => new Error('The exported file was empty.'));
          }
          return of(data);
        }),
        retry({
          count: 2,
          delay: (_error, retryCount) => timer(retryCount * 1000)
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (data) => {
          const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const job = this.job();
          const fileName = `Otap-${job.name.replace(/\s+/g, '-')}-${job.id}-${
            new Date().toISOString().split('.')[0]
          }Z.xlsx`;
          const anchor = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          anchor.href = url;
          anchor.download = fileName;
          anchor.click();
          anchor.remove();
          setTimeout(() => window.URL.revokeObjectURL(url), 0);
          this.exportState.set({ status: 'completed', exportId });
        },
        error: (error) => {
          console.error(`Unable to download OTAP export ${exportId}.`, error);
          this.failExport('The export was generated but could not be downloaded. Please try again.');
        }
      });
  }

  public updateExportStatusAndDownload(exportId: string, complete: boolean): void {
    if (!complete) {
      this.exportState.set({ status: 'waiting', exportId });
      return;
    }

    const state = this.exportState();
    if (state.status === 'waiting' && state.exportId === exportId) {
      this.downloadExportedFile(exportId);
    }
  }

  private failExport(message: string): void {
    this.exportState.set({ status: 'failed', message });
  }

  public onManuallyEnableStartButtons(): void {
    this.jobStartEnabled.emit(true);
  }

  public onOpenImportDialog(): void {
    if (this.importing()) return;

    const dialogRef = this.dialog.open(ImportGatewaysDialogComponent, {
      width: '420px',
      data: {},
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((buildingId?: number) => {
      if (!buildingId) return; // cancelled

      this.importing.set(true);

      this.gatewayService
        .getGateways(buildingId)
        .pipe(
          concatMap((res: Gateways) => {
            const list: Gateway[] = Array.isArray(res?.gateway) ? res.gateway : [];
            // filter only gateways that have a non-empty serialNumber
            const validList = list.filter((gw) => !!gw.serialNumber?.trim());
            if (!validList.length) return of(null);

            // Add each gateway sequentially
            return from(validList).pipe(
              concatMap((gw: Gateway) => {
                const payload: IOtapUpgradeGatewayDTO = {
                  serialNumber: gw.serialNumber!,
                  jobId: this.job().id
                };
                return this.otapUpgradeService
                  .addGatewayToJob(this.job().id, payload)
                  .pipe(tap(created => this.gateways.update(gateways => [...gateways, created])));
              })
            );
          }),
          finalize(() => this.importing.set(false))
        )
        .subscribe({
          next: () => {},
          error: (err) => {
            console.error('Import gateways failed', err);
            alert('Failed to import gateways for the selected building.');
          }
        });
    });
  }

  private markGatewayHealthUnknown(gateway: OtapUpgradeGatewayDTO): OtapUpgradeGatewayDTO {
    return {
      ...gateway,
      gatewayHealth: {
      notificationType: gateway.gatewayHealth?.notificationType ?? 'gateway-health',
      gatewayId: gateway.gatewayHealth?.gatewayId ?? gateway.id,
      timestamp: new Date().toISOString()
      }
    };
  }
}
